import { type FC, useEffect, useState } from "react";
import { Table } from "semantic-ui-react";
import { TextTruncate } from "../../../../components";
import { NoData } from "../../../../components/charts";
import { Tooltip } from "../../../../components/common/tooltip";
import { ListViewBase } from "../../../../components/listView";
import { type ColumnOption } from "../../../../interfaces/columnOptions";
import { PerformanceTabSectionList } from "../../../Library/Common/Performance/PerformanceTabSectionList/PerformanceTabSectionList";
import { type OwnerPacksLicensing } from "../../types/packs";
import utils from "utils/miscellaneousUtils";
import { type ConnectedProps, connect } from "react-redux";
import { type RootState } from "features/Application/globaltypes/redux";
import { useAddOnQuery } from "features/Reporting/Content/queries/useAddOnQuery";
import { AddOnIds } from "enums";
import { PerformanceLinkButton } from "features/Library/Common/Buttons/PerformanceLinkButton/PerformanceLinkButton";

import "./spPackReportList.scss";

export interface Props extends PropsFromRedux {
  reduxFinished: boolean;
  isPaid: boolean;
  ownerPackLicenseData: OwnerPacksLicensing[];
  onPackSelection: ({ id, name }: { id: number; name: string }) => void;
}

export const SpPackReportList: FC<Props> = ({
  reduxFinished,
  ownerPackLicenseData,
  onPackSelection,
  accountId,
  isPaid,
}) => {
  const [slicedEntities, setSlicedEntities] = useState<OwnerPacksLicensing[]>([]);

  const { data: addons } = useAddOnQuery(accountId);
  const hasCreateAccount = addons?.find((addon) => addon.id === AddOnIds.CreateAccounts)?.isEnabled;

  useEffect(() => {
    if (reduxFinished) {
      setSlicedEntities([...ownerPackLicenseData].slice(0, 10));
    }
  }, [ownerPackLicenseData, reduxFinished]);

  const baseColumns: ColumnOption<OwnerPacksLicensing>[] = [
    {
      name: "Pack",
      width: isPaid ? 3 : 6,
      render(item) {
        return (
          <PerformanceLinkButton
            onClick={() => {
              onPackSelection({ id: item.PackId, name: item.PackName });
            }}
          >
            <Tooltip target={<TextTruncate>{item.PackName}</TextTruncate>} content={item.PackName} />
          </PerformanceLinkButton>
        );
      },
    },
    {
      name: "Trials",
      width: 2,
      render(item) {
        return item.ActiveTrials.toLocaleString();
      },
    },
    {
      name: "Purchased",
      width: 2,
      render(item) {
        return item.ActiveCustomers.toLocaleString();
      },
    },
    {
      name: "Expired Trials",
      width: 3,
      render(item) {
        return item.ExpiredCustomers.toLocaleString();
      },
    },
    {
      name: "Total Accounts",
      width: isPaid ? 3 : 4,
      render(item) {
        return item.TotalCustomers.toLocaleString();
      },
    },
    {
      name: "Total Licenses",
      width: 3,
      render(item) {
        return utils.unlimitedNumber(item.TotalLicenses).toLocaleString();
      },
    },
    {
      name: "Total Licenses Issued",
      width: isPaid ? 3 : 4,
      render(item) {
        return utils.unlimitedNumber(item.IssuedLicenses).toLocaleString();
      },
    },
  ];

  const filteredColumns = isPaid
    ? baseColumns
    : baseColumns.filter(
        (col) => col.name === "Pack" || col.name === "Total Accounts" || col.name === "Total Licenses Issued",
      );

  const vendorColumn: ColumnOption<OwnerPacksLicensing> = {
    name: "Vendor",
    width: isPaid ? 2 : 6,
    render(item) {
      return (
        <div className="shortened-pack-name-cell">
          <Tooltip
            position="top left"
            target={<TextTruncate>{item.PackOwnerName}</TextTruncate>}
            content={item.PackOwnerName}
          />
        </div>
      );
    },
  };

  const displayedColumns = hasCreateAccount ? filteredColumns.toSpliced(1, 0, vendorColumn) : filteredColumns;

  const buildTableBody = (item: OwnerPacksLicensing) => {
    return displayedColumns.map((column, index) => (
      <Table.Cell className="table-body-cell" width={column.width} key={`${column.name}_${index}`}>
        {column.render(item)}
      </Table.Cell>
    ));
  };

  const getData = (skip: number, top: number) => {
    setSlicedEntities([...ownerPackLicenseData].slice(skip, skip + top));
  };

  return (
    <PerformanceTabSectionList
      title="Summary"
      count={ownerPackLicenseData.length}
      renderList={(page) => {
        const parsedData = slicedEntities.map((entity) => ({
          id: entity.PackId,
          ...entity,
        }));
        return ownerPackLicenseData.length ? (
          <ListViewBase
            columnOptions={displayedColumns}
            loadPage={getData}
            items={parsedData}
            itemsAmount={ownerPackLicenseData.length}
            buildTableBody={buildTableBody}
            withFooter={false}
            activePage={page}
            withSelection={false}
          />
        ) : (
          <NoData />
        );
      }}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const connector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SpPackReportList);
export default ConnectedComponent;
